/**
 * WelcomeModal.js
 *
 * First-time welcome overlay for new users.
 * Design: clean, minimal card on a blurred backdrop.
 * No particles, no floating orbs — focus is entirely on the CTA.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const WelcomeModal = ({ onStart, onSkip }) => {
  const { isDark } = useTheme();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Scroll to top when modal appears to avoid colliding with bottom cards
  useEffect(() => {
    if (visible) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [visible]);

  // ── Token set ─────────────────────────────────────────────────────────────
  const bg           = isDark ? '#0f1729' : '#ffffff';
  const border       = isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)';
  const shadow       = isDark
    ? '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
    : '0 16px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)';
  const textPrimary  = isDark ? '#e8edf5' : '#1a2035';
  const textSecondary = isDark ? 'rgba(232,237,245,0.55)' : '#64748b';
  const featureBg    = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)';
  const featureBorder = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const featureText  = isDark ? 'rgba(232,237,245,0.72)' : '#475569';
  const accentColor  = '#F87217';
  const backdropBg   = isDark ? 'rgba(5,10,22,0.8)' : 'rgba(15,23,41,0.35)';

  const features = [
    { label: '30+ Specialized Agents' },
    { label: 'Visual Workflow Builder' },
    { label: 'Real-time Analytics' },
    { label: 'Enterprise Security' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          background: backdropBg,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '80px',
          overflowY: 'auto',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wt-welcome-title"
      >
        {/* Modal card */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 20,
            padding: '36px 40px 32px',
            maxWidth: 480,
            width: '90%',
            boxShadow: shadow,
            textAlign: 'center',
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            opacity: visible ? 1 : 0,
            transition: 'transform 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease',
            marginBottom: '60px',
          }}
        >
          {/* Wordmark / logo area — using brand color block instead of emoji */}
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 22px',
            }}
          >
            {/* Simple SVG grid icon — represents the marketplace grid */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2"  y="2"  width="7" height="7" rx="1.5" fill="white"/>
              <rect x="11" y="2"  width="7" height="7" rx="1.5" fill="rgba(255,255,255,0.6)"/>
              <rect x="2"  y="11" width="7" height="7" rx="1.5" fill="rgba(255,255,255,0.6)"/>
              <rect x="11" y="11" width="7" height="7" rx="1.5" fill="rgba(255,255,255,0.6)"/>
            </svg>
          </div>

          {/* Headline */}
          <h2
            id="wt-welcome-title"
            style={{
              color: textPrimary,
              fontSize: 22,
              fontWeight: 700,
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
              fontFamily: "'Inter', system-ui, sans-serif",
              lineHeight: 1.3,
            }}
          >
            Welcome to the AI Marketplace
          </h2>

          <p
            style={{
              color: textSecondary,
              fontSize: 14,
              lineHeight: 1.65,
              margin: '0 0 26px',
            }}
          >
            Discover and deploy AI agents for your workflows. This tour covers the key areas
            in under 3 minutes.
          </p>

          {/* Feature highlights */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
              marginBottom: 28,
              textAlign: 'left',
            }}
          >
            {features.map(({ label }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: featureBg,
                  border: `1px solid ${featureBorder}`,
                }}
              >
                {/* Bullet — brand accent dot */}
                <div
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: accentColor,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: featureText,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={onStart}
              style={{
                width: '100%',
                padding: '12px 24px',
                borderRadius: 10,
                background: accentColor,
                border: 'none',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'opacity 0.15s ease',
                letterSpacing: '0.005em',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              aria-label="Start the product tour"
            >
              Start Tour
            </button>

            <button
              onClick={onSkip}
              style={{
                width: '100%',
                padding: '10px 24px',
                borderRadius: 10,
                background: 'transparent',
                border: `1px solid ${border}`,
                color: textSecondary,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'color 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = textPrimary;
                e.currentTarget.style.borderColor = isDark
                  ? 'rgba(255,255,255,0.18)'
                  : 'rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = textSecondary;
                e.currentTarget.style.borderColor = border;
              }}
              aria-label="Skip the tour"
            >
              I'll explore on my own
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelcomeModal;